// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Email-first home-realm discovery. The provider dashboard serves both client
// workforce and QH/Quantino operators, so we resolve the tenant + IdP per
// signing-in user rather than pinning one: enter work email → resolve → federated
// REDIRECT to the org's IdP → bootstrap (handled by auth-context). With a single
// resolved IdP (the norm) we redirect automatically — no extra button, since a
// top-level redirect (unlike a popup) needs no synchronous user gesture.
//
// Invite links carry `?email=&orgSlug=`. Org membership comes from the invite, not
// the email domain, so an org can invite any-domain emails (e.g. an @times.health
// address into Sun River). When `orgSlug` is present we resolve the tenant BY SLUG
// and skip domain discovery entirely — that's the only path that lets a custom-domain
// invitee sign in (their domain isn't mapped, so email discovery would 200 with an
// empty tenant and read as "org not found"). Domain discovery is the cold-start
// fallback used only when there's no `orgSlug`.

import { useState, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Users,
  Activity,
  Mail,
  Lock,
  Loader2,
  XCircle,
  ArrowLeft,
  Building2,
  FileHeart,
  ShieldCheck,
} from 'lucide-react'
import { Branding } from '@qh/design-tokens/branding'
import { LogoWordmark, LogoMark } from '@qh/design-tokens/logos'
import { Input } from '@/components/ui/input'
import { useTheme } from '@/context/theme-context'
import { useAuth, authErrorKey } from '@/context/auth-context'
import { readInviteParams, clearInviteParams } from '@/lib/invite'
import type { TenantResolution } from '@/types'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Remembers the last work email used to sign in (the user's own credentialing
// email — non-PHI), so the field is pre-filled next time. Overwritten on the
// next successful resolve; not cleared on sign-out (that's the point).
const LAST_EMAIL_KEY = 'qh.lastEmail'

function readLastEmail(): string {
  try {
    return localStorage.getItem(LAST_EMAIL_KEY) ?? ''
  } catch {
    return ''
  }
}

// Google "G" — inlined so no external asset fetch is needed.
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  )
}

// Resolver IdP id → button label key + icon (used only in the rare multi-IdP case).
function idpButton(idp: string): { labelKey: string; icon: React.ReactNode } {
  switch (idp) {
    case 'google':
      return { labelKey: 'login.continueWithGoogle', icon: <GoogleIcon /> }
    case 'microsoft':
    case 'microsoft.com':
      return { labelKey: 'login.continueWithMicrosoft', icon: <MicrosoftIcon /> }
    default:
      return { labelKey: 'login.continueWithSso', icon: <Building2 className="h-4 w-4" /> }
  }
}

// Resolved IdPs. When the resolver returns an explicit per-tenant list we honor it;
// otherwise default to Google + Microsoft so both buttons show (whichever the tenant
// actually has enabled in GCIP will work; the other errors cleanly if picked).
function idpsOf(res: TenantResolution): string[] {
  return res.idps && res.idps.length > 0 ? res.idps : ['google', 'microsoft']
}

function LeftInfoCard({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-4 flex items-center gap-4">
      <div className="h-11 w-11 rounded-xl bg-[#02C7C3]/25 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-[#02C7C3]" />
      </div>
      <div className="min-w-0">
        <p className="text-base font-semibold text-white">{title}</p>
        <p className="text-white/60 text-sm mt-0.5 leading-snug">{description}</p>
      </div>
    </div>
  )
}

export default function LoginPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading, authError, clearAuthError, resolveTenant, signInWithIdp, signInWithPassword } = useAuth()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  // Invite params win over the remembered email — this sign-in is for the invitee.
  const [invite] = useState(readInviteParams)
  const [email, setEmail] = useState(() => invite.email || readLastEmail())
  const [slug, setSlug] = useState('')
  const [showSlugFallback, setShowSlugFallback] = useState(false)
  const [resolution, setResolution] = useState<TenantResolution | null>(null)
  const [pending, setPending] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  // Email+password path (for testers / non-SSO members): shown alongside the IdP
  // buttons once a tenant resolves. settingPassword = first-login "set your password".
  const [password, setPassword] = useState('')
  const [settingPassword, setSettingPassword] = useState(false)
  const [pwBusy, setPwBusy] = useState(false)

  // Invite deep-link: an `orgSlug` in the URL resolves the tenant by slug and
  // auto-redirects, bypassing email-domain discovery so custom-domain invitees can
  // sign in. Fire once, and only while unauthenticated (skip during the redirect
  // round-trip and after sign-in). MUST be declared before any early return so the
  // hook order stays stable across the unauth→auth transition (React error #300).
  const inviteAttempted = useRef(false)
  useEffect(() => {
    if (inviteAttempted.current || !invite.orgSlug || authLoading || user) return
    inviteAttempted.current = true
    // One-shot: consume the stashed params so a failed federated redirect can't
    // re-trigger an auto-redirect loop when the user lands back on /login.
    clearInviteParams()
    void resolveAndRedirect({ slug: invite.orgSlug })
  }, [authLoading, user])

  // Already signed in → leave login. RequireAuth shows AccessDenied if the
  // account turns out to be unprovisioned.
  if (!authLoading && user) return <Navigate to="/dashboard" replace />

  // A local resolver error takes precedence; otherwise show a redirect-return error.
  const shownError = errorKey ?? authError

  // Pin + redirect to the IdP. Navigates away on success; only returns here on a
  // config error.
  async function startRedirect(idp: string, tenantId: string) {
    setRedirecting(true)
    setErrorKey(null)
    clearAuthError()
    try {
      // The typed email becomes the IdP login_hint (pre-selects the account).
      await signInWithIdp(idp, tenantId, email.trim() || undefined)
    } catch (err) {
      setErrorKey(authErrorKey(err))
      setRedirecting(false)
    }
  }

  // Resolve a tenant (by email OR by slug) and, on a single IdP, redirect straight
  // to it. `revealFallbackOnEmpty` shows the manual org-slug field when an email
  // lookup finds no org — only meaningful for the domain path, not the slug path.
  async function resolveAndRedirect(opts: { email?: string; slug?: string; revealFallbackOnEmpty?: boolean }) {
    setPending(true)
    setErrorKey(null)
    clearAuthError()
    try {
      // Keep the call single-arg on the email path (matches the resolver contract).
      const res = opts.email !== undefined
        ? await resolveTenant(opts.email)
        : await resolveTenant(undefined, opts.slug)
      // Unknown domains return a uniform 200 with an EMPTY tenantId — treat that
      // as "no org": show the contact-admin message and, after an email lookup,
      // reveal the org-slug fallback for cross-domain admins.
      if (!res.tenantId) {
        setErrorKey('login.orgNotFound')
        if (opts.revealFallbackOnEmpty) setShowSlugFallback(true)
        return
      }
      // Remember a work email that resolved to a real org, for next time.
      if (opts.email) {
        try {
          localStorage.setItem(LAST_EMAIL_KEY, opts.email)
        } catch {
          // localStorage unavailable — pre-fill simply won't persist.
        }
      }
      const idps = idpsOf(res)
      if (idps.length === 1) {
        // Single IdP (the norm) → redirect immediately, no extra click.
        await startRedirect(idps[0]!, res.tenantId)
      } else {
        // Rare: tenant offers multiple IdPs → let the user choose.
        setResolution(res)
        setShowSlugFallback(false)
      }
    } catch (err) {
      setErrorKey(authErrorKey(err))
    } finally {
      setPending(false)
    }
  }

  function runResolve(byEmail: boolean) {
    if (byEmail && !EMAIL_RE.test(email.trim())) {
      setErrorKey('login.invalidEmail')
      return
    }
    if (!byEmail && !slug.trim()) {
      setErrorKey('login.enterOrgSlug')
      return
    }
    void resolveAndRedirect(
      byEmail
        ? { email: email.trim(), revealFallbackOnEmpty: true }
        : { slug: slug.trim() },
    )
  }

  function backToEmail() {
    setResolution(null)
    setErrorKey(null)
    clearAuthError()
    setShowSlugFallback(false)
    setPassword('')
    setSettingPassword(false)
  }

  // Email+password sign-in (or first-login set-password) against the resolved tenant.
  // On success the auth-state listener bootstraps + routes; on user-not-found we flip to
  // the set-password affordance for invited members who haven't set one yet.
  async function submitPassword() {
    if (!resolution) return
    if (password.length < 6) {
      setErrorKey('login.passwordTooShort')
      return
    }
    setPwBusy(true)
    setErrorKey(null)
    clearAuthError()
    try {
      await signInWithPassword(email.trim(), password, resolution.tenantId, settingPassword)
    } catch (err) {
      const key = authErrorKey(err)
      if (key === 'login.userNotFound' && !settingPassword) {
        setSettingPassword(true)
        setErrorKey('login.setPasswordHint')
      } else {
        setErrorKey(key)
      }
      setPwBusy(false)
    }
  }

  // During the redirect bridge the body shows the "taking you to sign in" spinner,
  // so keep the subtitle on the normal copy (avoid duplicating the same line).
  const subtitle = resolution ? t('login.chooseSignIn') : t('login.signInSubtitle')

  return (
    <div className="min-h-svh flex overflow-hidden">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[58%] xl:w-[60%] relative flex-col justify-between p-10 xl:p-12 overflow-hidden bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-[380px] h-[380px] rounded-full bg-[#006EF9]/40 blur-3xl pointer-events-none" />
        <div className="absolute bottom-32 right-10 opacity-5 pointer-events-none">
          <Activity className="h-64 w-64 text-white" strokeWidth={0.5} />
        </div>

        <div className="relative z-10" style={{ filter: 'brightness(0) invert(1)' }}>
          <LogoWordmark className="h-9 w-auto" aria-hidden />
        </div>

        <div className="relative z-10 flex flex-col gap-10">
          <div>
            <p className="text-[#02C7C3] text-sm font-semibold uppercase tracking-widest mb-4">
              {t('login.providerPortal')}
            </p>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-5">{t('login.headline')}</h1>
            <p className="text-white/65 text-base leading-relaxed whitespace-nowrap">{t('login.subheadline')}</p>
          </div>
          <div className="flex flex-col gap-3 max-w-sm xl:max-w-md stagger-children">
            <div className="animate-fade-up">
              <LeftInfoCard
                icon={FileHeart}
                title={t('login.infoPatientPictureTitle')}
                description={t('login.infoPatientPictureDescription')}
              />
            </div>
            <div className="animate-fade-up">
              <LeftInfoCard
                icon={ShieldCheck}
                title={t('login.infoSecureAccessTitle')}
                description={t('login.infoSecureAccessDescription')}
              />
            </div>
            <div className="animate-fade-up">
              <LeftInfoCard
                icon={Users}
                title={t('login.infoCareTeamsTitle')}
                description={t('login.infoCareTeamsDescription')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className={['flex-1 flex flex-col items-center justify-center px-6 py-10 relative overflow-hidden', isDark ? 'bg-background' : 'bg-secondary'].join(' ')}>
        <div className="relative rounded-3xl border border-border bg-card overflow-hidden w-full max-w-md z-10 shadow-lg">
          <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />

          <div className="p-6 sm:p-10">
            <div className="flex flex-col items-center mb-7">
              <LogoMark className="h-12 w-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground">{t('login.welcomeBack')}</h2>
              <p className="text-sm text-muted-foreground mt-1 text-center">{subtitle}</p>
            </div>

            {shownError && (
              <div className="mb-5 flex items-center gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20">
                <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                <span className="text-sm text-destructive">{t(shownError)}</span>
              </div>
            )}

            {authLoading ? (
              /* ── Resolving auth state (incl. completing a redirect return) ── */
              <div className="flex flex-col items-center gap-3 py-6">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{t('login.signingIn')}</p>
              </div>
            ) : redirecting ? (
              /* ── Redirecting to the IdP ── */
              <div className="flex flex-col items-center gap-3 py-6">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{t('login.redirectingToSignIn')}</p>
              </div>
            ) : resolution ? (
              /* ── Multiple IdPs → let the user choose ── */
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary/60 border border-border">
                  <Building2 className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm text-muted-foreground truncate">
                    {t('login.signingInTo')}{' '}
                    <span className="text-foreground font-medium">{resolution.orgSlug}</span>
                  </span>
                </div>
                {idpsOf(resolution).map((idp) => {
                  const { labelKey, icon } = idpButton(idp)
                  return (
                    <button
                      key={idp}
                      type="button"
                      onClick={() => void startRedirect(idp, resolution.tenantId)}
                      className="w-full h-11 rounded-xl font-semibold text-sm text-foreground flex items-center justify-center gap-3 border border-border bg-card hover:bg-secondary transition-colors shadow-sm"
                    >
                      {icon}
                      {t(labelKey)}
                    </button>
                  )
                })}
                {/* Email + password (testers / non-SSO members) */}
                <div className="relative flex items-center gap-2">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('login.or')}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <div className="flex flex-col gap-2" onKeyDown={(e) => { if (e.key === 'Enter') void submitPassword() }}>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      type="password"
                      className="pl-10"
                      placeholder={settingPassword ? t('login.newPasswordPlaceholder') : t('login.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); if (shownError) { setErrorKey(null); clearAuthError() } }}
                      autoComplete={settingPassword ? 'new-password' : 'current-password'}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={pwBusy}
                    onClick={() => void submitPassword()}
                    className="w-full h-11 rounded-xl font-semibold text-sm text-primary-foreground bg-primary hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {pwBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {settingPassword ? t('login.setPasswordSubmit') : t('login.signInWithPassword')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSettingPassword((v) => !v); setErrorKey(null); clearAuthError() }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {settingPassword ? t('login.havePassword') : t('login.firstTimeSetPassword')}
                  </button>
                </div>
                <button onClick={backToEmail} className="w-full flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('login.useDifferentEmail')}
                </button>
              </div>
            ) : (
              /* ── Email step (+ optional org-slug fallback) ── */
              <div className="flex flex-col gap-4" onKeyDown={(e) => { if (e.key === 'Enter') void runResolve(!showSlugFallback) }}>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground">{t('login.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      type="email"
                      className="pl-10"
                      placeholder="you@your-clinic.org"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (shownError) { setErrorKey(null); clearAuthError() } }}
                    />
                  </div>
                </div>

                {showSlugFallback && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">{t('login.orgSlugLabel')}</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <Input
                        type="text"
                        className="pl-10"
                        placeholder={t('login.orgSlugPlaceholder')}
                        value={slug}
                        onChange={(e) => { setSlug(e.target.value); if (shownError) { setErrorKey(null); clearAuthError() } }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">{t('login.orgSlugHint')}</p>
                  </div>
                )}

                <button
                  onClick={() => void runResolve(!showSlugFallback)}
                  disabled={pending}
                  className="mt-1 w-full h-11 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-primary/20 bg-gradient-to-r from-[#006EF9] to-[#02C7C3]"
                >
                  {pending ? <><Loader2 className="h-4 w-4 animate-spin" />{t('login.checking')}</> : t('login.continue')}
                </button>
              </div>
            )}

            <div className="mt-7 pt-5 border-t border-border flex items-center justify-center gap-2">
              <Lock className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t('login.hipaaNote')}</span>
            </div>
          </div>
        </div>
        <Branding className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 px-4" />
      </div>
    </div>
  )
}
